import { useEffect, useMemo, useState } from 'react';

import gql from 'graphql-tag';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

export type CreatorInfo = {
	displayName: string;
	avatarUrl: string;
};

export type CreatorInfoMap = Map<string, CreatorInfo>;

const CreatorInfoQuery = gql`
	query useCreatorInfoQuery($accountIds: [ID!]!) {
		users(accountIds: $accountIds) {
			accountId
			name
			picture
		}
	}
`;

type AGGUser = {
	accountId: string;
	name: string | null;
	picture: string | null;
};

type CreatorInfoQueryData = {
	users?: (AGGUser | null)[] | null;
};

export const useCreatorInfo = (accountIds: string[]): CreatorInfoMap => {
	const [creatorInfoMap, setCreatorInfoMap] = useState<CreatorInfoMap>(new Map());

	const uniqueIds = useMemo(() => [...new Set(accountIds.filter(Boolean))], [accountIds]);

	useEffect(() => {
		if (uniqueIds.length === 0) {
			setCreatorInfoMap(new Map());
			return;
		}

		let cancelled = false;

		void (async () => {
			try {
				const { data, error } = await queryInterop<CreatorInfoQueryData, { accountIds: string[] }>({
					query: CreatorInfoQuery as unknown as Parameters<typeof queryInterop>[0]['query'],
					variables: { accountIds: uniqueIds },
					interopApolloAggClient: true,
				});

				if (cancelled) {
					return;
				}

				error?.graphQLErrors?.forEach((e) => markErrorAsHandled(e));

				const map: CreatorInfoMap = new Map();
				if (data?.users) {
					for (const user of data.users) {
						if (user?.accountId && user.name) {
							map.set(user.accountId, {
								displayName: user.name,
								avatarUrl: user.picture || `/wiki/aa-avatar/${user.accountId}`,
							});
						}
					}
				}
				setCreatorInfoMap(map);
			} catch (err) {
				// Non-critical: preview will just not show author info
				if (err instanceof Error) {
					markErrorAsHandled(err);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [uniqueIds]);

	return creatorInfoMap;
};
